import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { InstanceIdentifierInterceptorSkipHeader } from '../../instance-identifier-interceptor';
import { EducationHistoryModel } from './models/education-history-model';
import { EEOCModel } from './models/eeoc-model';
import { EmploymentHistoryModel } from './models/employment-history-model';
import { JobStatusModel } from './models/job-status-model';
import { JobRequest } from './models/JobRequest';
import { JobsListModel } from './models/JobsListModel';
import { ProfileModel } from './models/profile-model';
import { ReferenceModel } from './models/reference-model';
import { SurveyModel } from './models/SurveyModel';
import { ApplicantEvent } from './service-models/ApplicantEvent';
import { Instance } from './service-models/instance';
import { IterativeApplicationModel } from './service-models/IterativeApplicationModel';
import { JobBrandingModel } from './service-models/job-branding-model';
import { JobModel } from './service-models/JobModel';
import { ResumeModel } from './service-models/ResumeModel';
import { ReviewModel } from './service-models/ReviewModel';

@Injectable({
  providedIn: 'root'
})
export class JobService {
  apiGatewayUrl: string;

  constructor(private http: HttpClient) {
    this.apiGatewayUrl = environment().apiGatewayUrl;
  }

  getEducationHistory(jobId: number, applicationId: number, completionRequestId: string): Observable<EducationHistoryModel> {
    let params = new HttpParams();
    if (completionRequestId) {
      params = params.append('completionRequestId', completionRequestId);
    }

    return this.http.get<EducationHistoryModel>(
      `${this.apiGatewayUrl}jobs/${jobId}/education-history/${applicationId}`, { params });
  }

  getEmploymentHistory(jobId: any, applicationId: number, completionRequestId: string): Observable<EmploymentHistoryModel> {
    let params = new HttpParams();
    if (completionRequestId) {
      params = params.append('completionRequestId', completionRequestId);
    }

    return this.http.get<EmploymentHistoryModel>(
      `${this.apiGatewayUrl}jobs/${jobId}/employment-history/${applicationId}`, { params });
  }

  getReferences(jobId: any, applicationId: number, completionRequestId: string): Observable<ReferenceModel> {
    let params = new HttpParams();
    if (completionRequestId) {
      params = params.append('completionRequestId', completionRequestId);
    }

    return this.http.get<ReferenceModel>(`${this.apiGatewayUrl}jobs/${jobId}/references/${applicationId}`, {params});
  }
  
  getJob(id: number): Observable<JobModel> {
    return this.http.get<JobModel>(`${this.apiGatewayUrl}jobs/${id}`);
  }

  getJobBranding(jobId: number): Observable<JobBrandingModel> {
    return this.http.get<JobBrandingModel>(`${this.apiGatewayUrl}jobs/${jobId}/branding`);
  }

  getJobs(request: JobRequest): Observable<JobsListModel> {
    return this.http.get<JobsListModel>(`${this.apiGatewayUrl}jobs?${request.toQueryString()}`);
  }

  getJobStatus(id: number): Observable<JobStatusModel> {
    return this.http.get<JobStatusModel>(`${this.apiGatewayUrl}jobs/${id}/status`);
  }

  getResume(jobId: any, applicantId: string): Observable<ResumeModel> {
    return this.http.get<ResumeModel>(`${this.apiGatewayUrl}jobs/${jobId}/applicants/${applicantId}/resume`);
  }

  getProfile(jobId: any, applicationId: number): Observable<ProfileModel> {
    return this.http.get<ProfileModel>(`${this.apiGatewayUrl}jobs/${jobId}/profile/${applicationId}`);
  }
  
  getSurvey(jobId: any, applicationId: number, elementId: number, completionRequestId: string): Observable<SurveyModel> {
    let params = new HttpParams();
    if (completionRequestId) {
      params = params.append('completionRequestId', completionRequestId);
    }

    return this.http.get<SurveyModel>(`${this.apiGatewayUrl}jobs/${jobId}/survey/${applicationId}/elements/${elementId}`, { params });
  }

  getEEOCForm(jobId: any, applicationId: number, completionRequestId: string): Observable<EEOCModel> {
    let params = new HttpParams();
    if (completionRequestId) {
      params = params.append('completionRequestId', completionRequestId);
    }

    return this.http.get<any>(`${this.apiGatewayUrl}jobs/${jobId}/eeoc/${applicationId}`, {params});
  }

  trackApplicantEvent(jobId: number, event: ApplicantEvent): Observable<HttpResponse<any>> {
    return this.http.post<any>(`${this.apiGatewayUrl}jobs/${jobId}/applicant-events`, event);
  }
  
  getReview(jobId: any, applicationId: number, completionRequestId: string) {
    let params = new HttpParams();
    if (completionRequestId) {
      params = params.append('completionRequestId', completionRequestId);
    }

    return this.http.get<ReviewModel>(`${this.apiGatewayUrl}jobs/${jobId}/review/${applicationId}`, {params});
  }

  getIterativeApplicationParts(jobId: any, applicantId: string): Observable<IterativeApplicationModel> {
    return this.http.get<IterativeApplicationModel>(`${this.apiGatewayUrl}jobs/${jobId}/iterative-application-parts/${applicantId}`);
  }

  getPartOneCompletion(jobId: any, applicantId: string): Observable<IterativeApplicationModel> {
    return this.http.get<IterativeApplicationModel>(`${this.apiGatewayUrl}jobs/${jobId}/part-one-completion/${applicantId}`);
  }

  getJobInstance(jobId: number): Observable<Instance> {
    const headers = new HttpHeaders().set(InstanceIdentifierInterceptorSkipHeader, '');
    return this.http.get<Instance>(`${this.apiGatewayUrl}jobs/${jobId}/instance`, { headers });
  }
}
